import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { LeadExportBundle, ReportFilters } from '../../../core/models/report.model';
import { PermissionService } from '../../../core/services/permission.service';
import { ReportsService } from '../../../core/services/reports.service';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { CardActionsComponent } from '../../../shared/components/card/card-actions.component';
import { CardContentComponent } from '../../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../../shared/components/card/card-title.component';
import { CardComponent } from '../../../shared/components/card/card.component';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ReportFilterBarComponent } from '../../../shared/components/report-filter-bar/report-filter-bar.component';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { exportToCSV, exportToXLSX, generateFilename } from '../../../shared/utils/export.utils';
import { exportLeadsToZip } from '../../../shared/utils/zip-export.utils';

/**
 * "Full lead detail plus attachments for QuoteIQ/spreadsheet handoff" (design doc §8) --
 * the zip-bundling phase 5 deferred to this phase rather than building it twice.
 */
@Component({
  selector: 'app-lead-export',
  standalone: true,
  imports: [
    ButtonComponent,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    CardActionsComponent,
    IconComponent,
    ReportFilterBarComponent,
    SpinnerComponent,
    TranslatePipe,
  ],
  templateUrl: './lead-export.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./lead-export.component.css', '../../../../css/report-print.css'],
})
export class LeadExportComponent {
  private reportsService = inject(ReportsService);
  private permissionService = inject(PermissionService);

  includeAllUsers = this.permissionService.has('interaction.read-others');
  canExport = this.permissionService.has('report.export');

  bundles = signal<LeadExportBundle[]>([]);
  loading = signal(false);
  exporting = signal(false);

  attachmentCount = () => this.bundles().reduce((sum, b) => sum + b.attachments.length, 0);

  async onFiltersChange(filters: ReportFilters): Promise<void> {
    this.loading.set(true);
    try {
      this.bundles.set(await this.reportsService.getLeadExportBundles(filters, this.includeAllUsers));
    } finally {
      this.loading.set(false);
    }
  }

  exportCsv(): void {
    exportToCSV(this.bundles().map((b) => b.row), generateFilename('leads'));
  }

  exportXlsx(): void {
    exportToXLSX(this.bundles().map((b) => b.row), generateFilename('leads'), 'Leads');
  }

  async exportZip(): Promise<void> {
    this.exporting.set(true);
    try {
      await exportLeadsToZip(this.bundles(), generateFilename('leads'));
    } finally {
      this.exporting.set(false);
    }
  }
}
